import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const tokenFromUrl = params.get("token") || "";

    const [token, setToken] = useState(tokenFromUrl);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 1800);
        } catch (err) {
            setError(apiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-5 py-12">
            <div className="w-full max-w-[440px] fade-up">
                <Link
                    to="/login"
                    className="link-red inline-flex items-center gap-1 text-sm mb-6"
                >
                    <ArrowLeft size={14} />
                    Back to sign in
                </Link>
                <p className="eyebrow">Set a new password</p>
                <h1 className="h1 mt-3">Reset your password</h1>
                <p className="text-muted mt-3">
                    Choose a strong password at least 8 characters long.
                </p>

                {done ? (
                    <div
                        className="brand-card mt-8"
                        data-testid="reset-done"
                        style={{ borderColor: "rgba(46,125,50,0.4)" }}
                    >
                        <CheckCircle2 size={28} style={{ color: "var(--brand-success)" }} />
                        <h3 className="h3 mt-3">Password updated</h3>
                        <p className="text-muted mt-2 text-sm">
                            Redirecting you to sign in…
                        </p>
                    </div>
                ) : (
                    <form className="mt-8 flex flex-col gap-5" onSubmit={onSubmit}>
                        {!tokenFromUrl && (
                            <div>
                                <label className="field-label" htmlFor="token">
                                    Reset token
                                </label>
                                <input
                                    id="token"
                                    required
                                    value={token}
                                    onChange={(e) => setToken(e.target.value)}
                                    className="field-input"
                                    placeholder="Paste the token from your email"
                                    data-testid="reset-token-input"
                                />
                            </div>
                        )}
                        <div>
                            <label className="field-label" htmlFor="pw">
                                New password
                            </label>
                            <input
                                id="pw"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="field-input"
                                placeholder="At least 8 characters"
                                data-testid="reset-password-input"
                            />
                        </div>
                        <div>
                            <label className="field-label" htmlFor="cf">
                                Confirm new password
                            </label>
                            <input
                                id="cf"
                                type="password"
                                required
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="field-input"
                                data-testid="reset-confirm-input"
                            />
                        </div>
                        {error && (
                            <div className="field-error" data-testid="reset-error">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            className="btn btn-primary w-full"
                            disabled={loading}
                            data-testid="reset-submit-btn"
                        >
                            {loading ? "Updating…" : "Update password"}
                            {!loading && <ArrowRight size={16} />}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
